/**
 * 后台分片上传的纯规则：哪些文件走分片、分片怎么切、断点怎么认。
 */
/** 服务端分片大小，与 AliyunOssMultipartClient.PART_SIZE_BYTES 一致。 */
export const BACKGROUND_UPLOAD_PART_BYTES = 8 * 1024 * 1024;
/** 图片和音频小于该体积时仍走原来的整文件上传。 */
export const DIRECT_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
/** 单片失败后的最多尝试次数，含第一次。 */
export const PART_UPLOAD_ATTEMPTS = 4;
/**
 * 视频一律后台分片；其他类型只有超过 20MB 才分片。
 * @param file 本地文件
 * @param assetDataType 素材类型编码：2-图片，3-视频，4-音频
 * @returns 需要后台分片时为 true
 */
export function shouldUploadInBackground(file, assetDataType) {
    return assetDataType === '3' || file.size > DIRECT_UPLOAD_MAX_BYTES;
}
/**
 * 按分片大小把文件切成连续区间。
 * @param fileSize 文件字节数
 * @param partSize 服务端返回的分片大小；非法时回退到默认 8MB
 * @returns 从 1 开始的分片区间
 */
export function planPartRanges(fileSize, partSize) {
    /** 实际使用的分片大小，避免服务端返回 0 时死循环。 */
    const size = partSize > 0 ? partSize : BACKGROUND_UPLOAD_PART_BYTES;
    /** 切出来的分片区间。 */
    const ranges = [];
    /** 当前分片起点。 */
    let start = 0;
    /** 当前分片号。 */
    let partNumber = 1;
    while (start < fileSize) {
        const end = Math.min(fileSize, start + size);
        ranges.push({ partNumber, start, end });
        start = end;
        partNumber += 1;
    }
    return ranges;
}
/**
 * 刷新后只能靠文件名、大小和修改时间判断是不是同一个文件。
 * @param saved 断点里记下的文件身份
 * @param file 用户重新选择的文件
 * @returns 三者一致时为 true
 */
export function isSameUploadFile(saved, file) {
    return (saved.name === file.name && saved.size === file.size && saved.lastModified === file.lastModified);
}
/**
 * 计算下一次单片重试的等待时间，上限 30 秒。
 * @param failedAttempt 已经失败的次数，从 1 开始
 * @returns 等待毫秒数
 */
export function partRetryDelayMs(failedAttempt) {
    return Math.min(30_000, 1000 * 2 ** Math.max(0, failedAttempt - 1));
}
/**
 * 内存里还有文件，或浏览器还留着文件句柄时，点击重试即可继续。
 * 两者都没有时才要求重新选择原文件。
 * @param hasMemoryFile 本次页面还留着 File
 * @param hasHandle 刷新后还能从 IndexedDB 读回句柄
 * @returns 进度条按钮：retry 为重试，reselect 为选择原文件
 */
export function resumeControlForUpload(hasMemoryFile, hasHandle) {
    return hasMemoryFile || hasHandle ? 'retry' : 'reselect';
}
/**
 * 只要还有未完成任务就展示进度面板，一条和多条共用同一块。
 * @param taskCount 当前未完成任务数
 * @returns 至少一条时为 true
 */
export function shouldShowUploadProgressTray(taskCount) {
    return taskCount > 0;
}
/**
 * 文件地址还没写回时，用本地首帧代替「待生成」。已有文件地址后只用服务端封面。
 * @param input 文件是否落库、服务端封面和本地首帧
 * @returns 卡片封面地址和占位文案
 */
export function resolveUploadingAssetCover(input) {
    if (!input.hasFile && input.localPosterUrl) {
        return { previewUrl: input.localPosterUrl, placeholderLabel: '待生成' };
    }
    if (!input.hasFile) {
        return { previewUrl: '', placeholderLabel: '待生成' };
    }
    return {
        previewUrl: input.serverPreviewUrl,
        placeholderLabel: '暂无封面',
    };
}
/**
 * 服务端胶片条还没就绪时，用本地首帧铺时间轴。胶片条就绪后不再用本地帧。
 * @param serverFrameUrl 已就绪的胶片条地址；没有时为空
 * @param localPosterUrl 本地首帧；没有时为空
 * @returns 片段上要画的地址；两者都没有时为空
 */
export function resolveTimelineUploadFrame(serverFrameUrl, localPosterUrl) {
    const url = serverFrameUrl ?? localPosterUrl;
    return url ? { url } : null;
}
/**
 * 把尚未完成的素材名收成发布拦截文案。没有未完成素材时返回空。
 * @param fileNames 仍在上传、失败或等待重新选择的文件名
 * @returns 发布弹窗使用的阻断说明
 */
export function blockingUploadMessage(fileNames) {
    if (fileNames.length === 0)
        return null;
    /** 文案里最多点名三个文件。 */
    const preview = fileNames.slice(0, 3).join('、');
    /** 超出三个时在名称后补「等」。 */
    const suffix = fileNames.length > 3 ? ' 等' : '';
    return `素材「${preview}${suffix}」还在上传或上传失败，完成后再发布`;
}
